import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Trash2, Save, Building2, Users, Settings, Eye, Edit, ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useAuth from "../../contexts/AuthContext";
import Loader from "../../common_components/Loader";
import { formatDateForInput } from "../../utils/date";
import { useToast } from "../../common_components/Toast";
import { createMasterDataService } from "../../services/masterDataService";
import {
  FACULTY_DEFAULT_SCHEMA,
  NON_FACULTY_DEFAULT_SCHEMA,
  isCoreField
} from "../dynamic-form/constants/presets";

export default function RecruitmentBuilder() {
  const { apiRequest } = useAuth();
  const masterDataService = useMemo(() => createMasterDataService({ apiRequest }), [apiRequest]);
  const toast = useToast();
  const navigate = useNavigate();
  const { recruitmentId } = useParams();
  const location = useLocation();

  // Determine type from URL path
  const isNonFaculty = location.pathname.includes("non-faculty");
  const recruitmentType = isNonFaculty ? "NON_FACULTY" : "FACULTY";
  const apiPrefix = isNonFaculty ? "/non-faculty" : "/faculty";
  const redirectUrl = isNonFaculty ? "/non-faculty/admin/recruitment/all" : "/faculty/admin/recruitment/all";

  const [activeTab, setActiveTab] = useState("general"); // general, payment, departments, formBuilder
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  // Master Data State
  const [masterDepartments, setMasterDepartments] = useState<any[]>([]);
  const [masterPosts, setMasterPosts] = useState<any[]>([]);

  // Main recruitment state
  const [formData, setFormData] = useState<any>({
    title: "",
    description: "",
    departments: [] as any[],
    applicationDates: {
      startDate: "",
      endDate: "",
    },
    paymentDetails: {
      amount: {
        UR: "",
        OBC: "",
        SC: "",
        ST: "",
        EWS: "",
        PWD: "",
        FEMALE: "",
        EX_SERVICEMAN: "",
      } as Record<string, string | number>,
      currency: "INR",
      modes: {
        online: { enabled: false },
        offline: { enabled: true, requireUTR: true, requireScreenshot: true },
      },
    },
    generalRequirements: {
      councilRegistration: { required: false },
      ageLimit: { max: 65, relaxation: { OBC: 3, SC: 5, ST: 5, PWD: 10 } as Record<string, number> },
    },
    status: "DRAFT",
    applicationFormSchema: isNonFaculty ? { ...NON_FACULTY_DEFAULT_SCHEMA } : { ...FACULTY_DEFAULT_SCHEMA },
  });

  // State for Form Builder UI
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null); // sectionId.fieldId
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [draggedField, setDraggedField] = useState<any | null>(null); // { sectionId, index }

  const fetchMasterData = useCallback(async () => {
    try {
      const [depRes, postRes] = await Promise.all([
        masterDataService.getDepartments(),
        masterDataService.getDesignations()
      ]);
      setMasterDepartments(depRes.data || []);
      setMasterPosts(postRes.data || []);
    } catch (err) {
      console.error("Failed to fetch master data:", err);
      toast.error("Failed to load departments and posts");
    }
  }, [masterDataService, toast]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  const fetchRecruitmentData = useCallback(async () => {
    setFetching(true);
    setError(null);
    try {
      const res = await apiRequest(`${apiPrefix}/recruitment/${recruitmentId}`, {
        method: "GET",
      });
      const data = res?.recruitment;
      setFormData({
        title: data.title || "",
        description: data.description || "",
        departments: data.departments || [],
        applicationDates: {
          startDate: formatDateForInput(data.applicationDates?.startDate) || "",
          endDate: formatDateForInput(data.applicationDates?.endDate) || "",
        },
        paymentDetails: {
          amount: (() => {
            const raw = data.paymentDetails?.amount || {};
            const amt: Record<string, any> = {
              UR: raw.UR ?? "",
              OBC: raw.OBC ?? "",
              SC: raw.SC ?? "",
              ST: raw.ST ?? "",
              EWS: raw.EWS ?? "",
            };
            if ("PWD" in raw && raw.PWD !== undefined && raw.PWD !== null) amt.PWD = raw.PWD;
            if ("FEMALE" in raw && raw.FEMALE !== undefined && raw.FEMALE !== null) amt.FEMALE = raw.FEMALE;
            if ("EX_SERVICEMAN" in raw && raw.EX_SERVICEMAN !== undefined && raw.EX_SERVICEMAN !== null) {
              amt.EX_SERVICEMAN = raw.EX_SERVICEMAN;
            } else if ("EX_SERVICEMEN" in raw && raw.EX_SERVICEMEN !== undefined && raw.EX_SERVICEMEN !== null) {
              amt.EX_SERVICEMAN = raw.EX_SERVICEMEN;
            }
            return amt;
          })(),
          currency: data.paymentDetails?.currency || "INR",
          modes: data.paymentDetails?.modes || {
            online: { enabled: false },
            offline: { enabled: true, requireUTR: true, requireScreenshot: true },
          },
        },
        generalRequirements: data.generalRequirements || {
          councilRegistration: { required: false },
          ageLimit: { max: 65, relaxation: { OBC: 3, SC: 5, ST: 5, PWD: 10 } },
        },
        status: data.status || "DRAFT",
        applicationFormSchema: data.applicationFormSchema || (isNonFaculty ? { ...NON_FACULTY_DEFAULT_SCHEMA } : { ...FACULTY_DEFAULT_SCHEMA }),
      });
    } catch (err: any) {
      setError(err.message || "Failed to fetch recruitment data");
      console.error("Error fetching recruitment:", err);
    } finally {
      setFetching(false);
    }
  }, [apiRequest, apiPrefix, isNonFaculty, recruitmentId]);

  useEffect(() => {
    if (recruitmentId) {
      fetchRecruitmentData();
    }
  }, [recruitmentId, fetchRecruitmentData]);

  const handlePaymentAmountChange = (category: string, value: string) => {
    const numericValue = parseInt(value, 10);
    const newAmount = !isNaN(numericValue) && numericValue >= 0 ? numericValue : "";

    setFormData((prev: any) => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        amount: {
          ...prev.paymentDetails?.amount,
          [category]: newAmount,
        },
      },
    }));
  };

  const removePaymentCategory = (category: string) => {
    if (!["PWD", "FEMALE", "EX_SERVICEMAN"].includes(category)) return;
    setFormData((prev: any) => {
      const updatedAmount = { ...(prev.paymentDetails?.amount || {}) };
      delete updatedAmount[category];
      return {
        ...prev,
        paymentDetails: {
          ...prev.paymentDetails,
          amount: updatedAmount,
        },
      };
    });
  };

  const addPaymentCategory = (category: string) => {
    setFormData((prev: any) => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        amount: {
          ...(prev.paymentDetails?.amount || {}),
          [category]: "",
        },
      },
    }));
  };

  const OPTIONAL_SPECIAL_CATEGORIES = ["PWD", "FEMALE", "EX_SERVICEMAN"];
  const missingFeeCategories = OPTIONAL_SPECIAL_CATEGORIES.filter(
    (cat) => !formData.paymentDetails?.amount || !(cat in formData.paymentDetails.amount)
  );

  const addDepartment = () => {
    setFormData({
      ...formData,
      departments: [
        ...formData.departments,
        {
          name: "",
          candidateType: "ANY",
          vacancies: [
            {
              post: masterPosts.length > 0 ? masterPosts[0].name : "",
              categories: { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 },
              femaleSeats: 0,
              pwdSeats: 0,
              requirements: {
                graduationQualifications: [] as any[],
                postGraduationQualifications: [{ combination: [] as any[], type: "OR" }],
                PhD: [] as any[],
                superSpecializationQualifications: [] as any[],
                minExperienceYears: 0,
                experienceConditions: [] as any[],
                additionalRequirements: [] as any[],
              },
            },
          ],
        },
      ],
    });
  };

  const removeDepartment = (deptIndex: number) => {
    setFormData({
      ...formData,
      departments: formData.departments.filter((_: any, i: number) => i !== deptIndex),
    });
  };

  const updateDepartment = (deptIndex: number, field: string, value: any) => {
    const updated = [...formData.departments];
    updated[deptIndex][field] = value;
    setFormData({ ...formData, departments: updated });
  };

  const addVacancy = (deptIndex: number) => {
    const updated = [...formData.departments];
    updated[deptIndex].vacancies.push({
      post: masterPosts.length > 0 ? masterPosts[0].name : "",
      categories: { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 },
      femaleSeats: 0,
      pwdSeats: 0,
      requirements: {
        graduationQualifications: [] as any[],
        postGraduationQualifications: [{ combination: [] as any[], type: "OR" }],
        PhD: [] as any[],
        superSpecializationQualifications: [] as any[],
        minExperienceYears: 0,
        experienceConditions: [] as any[],
        additionalRequirements: [] as any[],
      },
    });
    setFormData({ ...formData, departments: updated });
  };

  const removeVacancy = (deptIndex: number, vacIndex: number) => {
    const updated = [...formData.departments];
    updated[deptIndex].vacancies = updated[deptIndex].vacancies.filter((_: any, i: number) => i !== vacIndex);
    setFormData({ ...formData, departments: updated });
  };

  const updateVacancy = (deptIndex: number, vacIndex: number, field: string, value: any) => {
    const updated = [...formData.departments];
    updated[deptIndex].vacancies[vacIndex][field] = value;
    setFormData({ ...formData, departments: updated });
  };

  const updateVacancyCategory = (deptIndex: number, vacIndex: number, category: string, value: string) => {
    const updated = [...formData.departments];
    updated[deptIndex].vacancies[vacIndex].categories[category] = parseInt(value) || 0;
    setFormData({ ...formData, departments: updated });
  };

  // --- Dynamic Schema Builder Logic ---

  const handleLoadPreset = () => {
    const preset = isNonFaculty ? { ...NON_FACULTY_DEFAULT_SCHEMA } : { ...FACULTY_DEFAULT_SCHEMA };
    setFormData({
      ...formData,
      applicationFormSchema: JSON.parse(JSON.stringify(preset))
    });
    toast.success("Default preset loaded!");
  };

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const addCustomSection = () => {
    const sections = [...formData.applicationFormSchema.sections];
    const newId = `section_${Date.now()}`;
    sections.push({
      id: newId,
      title: "New Custom Section",
      description: "",
      removable: true,
      fields: [] as any[]
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
    setCollapsedSections(prev => ({ ...prev, [newId]: false }));
  };

  const deleteSection = (sectionId: string) => {
    const sections = formData.applicationFormSchema.sections.filter((s: any) => s.id !== sectionId);
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const updateSectionTitle = (sectionId: string, value: string) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) return { ...s, title: value };
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const updateSectionDesc = (sectionId: string, value: string) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) return { ...s, description: value };
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const addCustomField = (sectionId: string) => {
    const newFieldId = `field_${Date.now()}`;
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields ? [...s.fields] : [];
        fields.push({
          id: newFieldId,
          label: "New Custom Field",
          type: "text",
          required: false,
          editable: true
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
    setEditingFieldId(`${sectionId}.${newFieldId}`);
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields.filter((f: any) => f.id !== fieldId);
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const updateFieldProperty = (sectionId: string, fieldId: string, propName: string, value: any) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f: any) => {
          if (f.id === fieldId) {
            const updated = { ...f, [propName]: value };
            // Clear specific settings if type changes
            if (propName === "type") {
              if (value !== "select") delete updated.options;
              if (value !== "repeater") delete updated.fields;
              if (value !== "number" && value !== "text") delete updated.validations;
            }
            return updated;
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const updateFieldValidation = (sectionId: string, fieldId: string, valProp: string, value: any) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f: any) => {
          if (f.id === fieldId) {
            const validations = { ...(f.validations || {}), [valProp]: value };
            if (value === "" || value === undefined) {
              delete validations[valProp];
            }
            return { ...f, validations };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const handleAddSelectOption = (sectionId: string, fieldId: string, newOpt: string) => {
    if (!newOpt.trim()) return;
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f: any) => {
          if (f.id === fieldId) {
            const options = [...(f.options || [])];
            if (!options.includes(newOpt)) options.push(newOpt);
            return { ...f, options };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  const handleRemoveSelectOption = (sectionId: string, fieldId: string, optToRemove: string) => {
    const sections = formData.applicationFormSchema.sections.map((s: any) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f: any) => {
          if (f.id === fieldId) {
            const options = (f.options || []).filter((o: any) => o !== optToRemove);
            return { ...f, options };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections }
    });
  };

  // --- Drag and Drop Handlers for Form Builder Fields ---
  const handleDragStart = (e: React.DragEvent, sectionId: string, index: number) => {
    setDraggedField({ sectionId, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedField(null);
  };

  const handleDrop = (e: React.DragEvent, targetSectionId: string, targetIndex: number) => {
    e.preventDefault();
    if (!draggedField) return;

    const sourceSectionId = draggedField.sectionId;
    const sourceIndex = draggedField.index;

    // Do nothing if dropped on the exact same spot
    if (sourceSectionId === targetSectionId && sourceIndex === targetIndex) {
      setDraggedField(null);
      return;
    }

    const sections = JSON.parse(JSON.stringify(formData.applicationFormSchema.sections));
    const sourceSection = sections.find((s: any) => s.id === sourceSectionId);
    const targetSection = sections.find((s: any) => s.id === targetSectionId);

    if (!sourceSection || !targetSection) {
      setDraggedField(null);
      return;
    }

    // Move the field
    const [movedField] = sourceSection.fields.splice(sourceIndex, 1);
    targetSection.fields.splice(targetIndex, 0, movedField);

    setFormData({
      ...formData,
      applicationFormSchema: {
        ...formData.applicationFormSchema,
        sections
      }
    });

    setDraggedField(null);
    toast.success("Field order updated!");
  };

  // --- Submission ---

  const handleSubmit = async (publish = false) => {
    setLoading(true);
    setError(null);

    try {
      const cleanedAmount: Record<string, any> = {};
      if (formData.paymentDetails?.amount) {
        for (const [k, v] of Object.entries(formData.paymentDetails.amount)) {
          if (v !== "" && v !== null && v !== undefined && !isNaN(Number(v))) {
            cleanedAmount[k] = Number(v);
          }
        }
      }

      const dataToSubmit = {
        ...formData,
        paymentDetails: {
          ...formData.paymentDetails,
          amount: cleanedAmount,
        },
        status: publish ? "PUBLISHED" : "DRAFT",
      };

      if (!dataToSubmit.title || !dataToSubmit.applicationDates.startDate || !dataToSubmit.applicationDates.endDate) {
        toast.error("Please fill in the required fields (Title, Start Date, End Date).");
        setLoading(false);
        return;
      }

      if (dataToSubmit.departments.length === 0) {
        toast.error("Please add at least one department.");
        setLoading(false);
        return;
      }

      // Validate each department has name and candidateType
      for (let dept of dataToSubmit.departments) {
        if (!dept.name || !dept.candidateType) {
          toast.error("Each department must have a name and candidate type.");
          setLoading(false);
          return;
        }
      }

      // Validate dynamic schema structure
      const sections = dataToSubmit.applicationFormSchema?.sections;
      if (!sections || !Array.isArray(sections) || sections.length === 0) {
        toast.error("Application form schema must contain at least one section.");
        setLoading(false);
        return;
      }

      // Determine if creating or updating
      const url = recruitmentId
        ? `${apiPrefix}/recruitment/${recruitmentId}`
        : `${apiPrefix}/recruitment`;

      const method = recruitmentId ? "PUT" : "POST";

      await apiRequest(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSubmit),
      });

      toast.success(
        recruitmentId
          ? publish
            ? "Recruitment updated and published successfully!"
            : "Recruitment updated successfully!"
          : publish
          ? "Recruitment published successfully!"
          : "Recruitment saved as draft!"
      );

      navigate(redirectUrl);
    } catch (err: any) {
      console.error("Error saving recruitment:", err);
      setError(err.message || "An error occurred");
      toast.error(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-serif text-slate-900 mb-2 tracking-tight leading-tight">
              {recruitmentId ? "Update Recruitment Builder" : "Create Recruitment Builder"}
            </h1>
            <p className="text-md text-slate-600">
              Configure details, vacancies, fees, and dynamic application forms for <strong>{recruitmentType}</strong> recruitment.
            </p>
          </div>
          <button
            onClick={() => navigate(redirectUrl)}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg shadow-sm text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
        </motion.div>

        {/* Tab Headers */}
        <div className="flex border-b border-gray-250 mb-6 bg-white p-2 rounded-xl border border-gray-200 shadow-sm gap-2">
          {[
            { id: "general", label: "General & Age Limits", icon: Building2 },
            { id: "payment", label: "Fees & Payment", icon: Users },
            { id: "departments", label: "Departments & Vacancies", icon: Building2 },
            { id: "formBuilder", label: "Application Form Builder", icon: Settings },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-blue-600 hover:bg-slate-50"
                }`}
              >
                <Icon className="w-4.5 h-4.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === "general" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 border-b pb-3">Basic Info & Date Settings</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Recruitment Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. Faculty Recruitment 2026 Phase-1"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                    placeholder="Provide a detailed description"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Start Date *</label>
                  <input
                    type="date"
                    value={formData.applicationDates.startDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applicationDates: { ...formData.applicationDates, startDate: e.target.value },
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">End Date *</label>
                  <input
                    type="date"
                    value={formData.applicationDates.endDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        applicationDates: { ...formData.applicationDates, endDate: e.target.value },
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <h2 className="text-xl font-bold text-slate-900 border-b pb-3 pt-4">Age Limits & Requirements</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Maximum Age (General)</label>
                  <input
                    type="number"
                    value={formData.generalRequirements.ageLimit.max}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        generalRequirements: {
                          ...formData.generalRequirements,
                          ageLimit: { ...formData.generalRequirements.ageLimit, max: parseInt(e.target.value) || "" },
                        },
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.generalRequirements.councilRegistration.required}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          generalRequirements: {
                            ...formData.generalRequirements,
                            councilRegistration: { required: e.target.checked },
                          },
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-gray-700">Require Council Registration?</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-700">Age Relaxations (Years)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {["OBC", "SC", "ST", "PWD"].map((cat) => (
                    <div key={cat} className="space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">{cat} Relaxation</label>
                      <input
                        type="number"
                        value={formData.generalRequirements.ageLimit.relaxation[cat] || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            generalRequirements: {
                              ...formData.generalRequirements,
                              ageLimit: {
                                ...formData.generalRequirements.ageLimit,
                                relaxation: {
                                  ...formData.generalRequirements.ageLimit.relaxation,
                                  [cat]: parseInt(e.target.value) || 0,
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "payment" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-3 gap-3">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Application Fees</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Configure fee amounts. You can delete optional category fields (Female, PwD, Ex-Servicemen) if fee exemptions are not applicable.</p>
                </div>
                {missingFeeCategories.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-500">Add Fee Category:</span>
                    {missingFeeCategories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => addPaymentCategory(cat)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {cat === "EX_SERVICEMAN" ? "EX-SERVICEMAN" : cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Object.keys(formData.paymentDetails?.amount || {}).map((category) => {
                  const isOptional = ["PWD", "FEMALE", "EX_SERVICEMAN"].includes(category);
                  return (
                    <div key={category} className="space-y-2 p-3 bg-slate-50 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-bold text-gray-700 uppercase">
                          {category === "EX_SERVICEMAN" ? "EX-SERVICEMAN" : category} Fee (₹)
                        </label>
                        {isOptional && (
                          <button
                            type="button"
                            onClick={() => removePaymentCategory(category)}
                            title={`Delete ${category} Fee Category`}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={formData.paymentDetails.amount[category] ?? ""}
                        onChange={(e) => handlePaymentAmountChange(category, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                        placeholder="e.g. 1500"
                      />
                    </div>
                  );
                })}
              </div>

              <h2 className="text-xl font-bold text-slate-900 border-b pb-3 pt-4">Payment Methods</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-slate-50 border rounded-xl space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer font-bold text-gray-800">
                    <input
                      type="checkbox"
                      checked={formData.paymentDetails.modes.offline.enabled}
                      onChange={(e) => {
                        const updated = { ...formData.paymentDetails };
                        updated.modes.offline.enabled = e.target.checked;
                        setFormData({ ...formData, paymentDetails: updated });
                      }}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    Offline Payment (Bank Transfer)
                  </label>
                  {formData.paymentDetails.modes.offline.enabled && (
                    <div className="space-y-4 pt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.paymentDetails.modes.offline.requireUTR}
                          onChange={(e) => {
                            const updated = { ...formData.paymentDetails };
                            updated.modes.offline.requireUTR = e.target.checked;
                            setFormData({ ...formData, paymentDetails: updated });
                          }}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">Require Transaction UTR Number</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.paymentDetails.modes.offline.requireScreenshot}
                          onChange={(e) => {
                            const updated = { ...formData.paymentDetails };
                            updated.modes.offline.requireScreenshot = e.target.checked;
                            setFormData({ ...formData, paymentDetails: updated });
                          }}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">Require Receipt Screenshot File Upload</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "departments" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
              <div className="flex items-center justify-between border-b pb-3">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Departments & Vacancies Configuration
                </h2>
                <button
                  type="button"
                  onClick={addDepartment}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm text-sm font-semibold transition-colors"
                >
                  <Plus className="w-4.5 h-4.5" /> Add Department
                </button>
              </div>

              {formData.departments.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                  <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 mb-3">No departments configured yet.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {formData.departments.map((dept: any, deptIndex: number) => (
                    <div key={deptIndex} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-6 relative">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-800">Department #{deptIndex + 1}</h3>
                        <button
                          type="button"
                          onClick={() => removeDepartment(deptIndex)}
                          className="flex items-center gap-1 text-xs font-bold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg border border-red-150 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Remove
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">Department Name *</label>
                          <select
                            value={dept.name}
                            onChange={(e) => updateDepartment(deptIndex, "name", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select Department</option>
                            {masterDepartments.map((d: any) => (
                              <option key={d._id} value={d.name}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">Candidate Type Requirement *</label>
                          <select
                            value={dept.candidateType}
                            onChange={(e) => updateDepartment(deptIndex, "candidateType", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="ANY">Any Type</option>
                            <option value="MEDICAL">Medical Only</option>
                            <option value="NON-MEDICAL">Non-Medical Only</option>
                          </select>
                        </div>
                      </div>

                      {/* Vacancies Sub List */}
                      <div className="space-y-4 pt-4 border-t border-gray-250">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-700 flex items-center gap-2">
                            <Users className="w-4 h-4 text-purple-600" /> Posts Vacancies
                          </h4>
                          <button
                            type="button"
                            onClick={() => addVacancy(deptIndex)}
                            className="text-xs flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 font-semibold rounded-lg hover:bg-purple-200 transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" /> Add Post
                          </button>
                        </div>

                        {dept.vacancies.map((vacancy: any, vacIndex: number) => (
                          <div key={vacIndex} className="bg-white rounded-xl p-5 border border-purple-150 shadow-xs space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-purple-500 uppercase tracking-wider">Post #{vacIndex + 1}</span>
                              <button
                                type="button"
                                onClick={() => removeVacancy(deptIndex, vacIndex)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-600">Select Post Designation *</label>
                                <select
                                  value={vacancy.post}
                                  onChange={(e) => updateVacancy(deptIndex, vacIndex, "post", e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                  <option value="">Select Post</option>
                                  {masterPosts.map((post: any) => (
                                    <option key={post._id} value={post.name}>
                                      {post.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-600">Min. Experience (Years)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={vacancy.requirements.minExperienceYears ?? ""}
                                  onChange={(e) => {
                                    const updated = [...formData.departments];
                                    updated[deptIndex].vacancies[vacIndex].requirements.minExperienceYears = e.target.value === "" ? 0 : parseInt(e.target.value);
                                    setFormData({ ...formData, departments: updated });
                                  }}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </div>
                            </div>

                            <div className="space-y-2 pt-2">
                              <label className="block text-xs font-semibold text-gray-500">Category Seat Matrix</label>
                              <div className="grid grid-cols-5 gap-3">
                                {["UR", "OBC", "SC", "ST", "EWS"].map((category) => (
                                  <div key={category} className="space-y-1">
                                    <label className="block text-center text-[10px] font-bold text-gray-500">{category}</label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={vacancy.categories[category] || ""}
                                      onChange={(e) => updateVacancyCategory(deptIndex, vacIndex, category, e.target.value)}
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-xs"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2">
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500">Reserved for Female (Horizontal)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={vacancy.femaleSeats ?? 0}
                                  onChange={(e) => updateVacancy(deptIndex, vacIndex, "femaleSeats", parseInt(e.target.value) || 0)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500">Reserved for PwD (Horizontal)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={vacancy.pwdSeats ?? 0}
                                  onChange={(e) => updateVacancy(deptIndex, vacIndex, "pwdSeats", parseInt(e.target.value) || 0)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "formBuilder" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Application Form Designer</h2>
                  <p className="text-sm text-slate-500 mt-1">Configure layout, sections, and fields for application forms.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleLoadPreset}
                    className="px-4 py-2 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                  >
                    Reset Form to Preset Default
                  </button>
                  <button
                    type="button"
                    onClick={addCustomSection}
                    className="flex items-center gap-1 px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Section
                  </button>
                </div>
              </div>

              {/* Sections Accordion */}
              <div className="space-y-4">
                {formData.applicationFormSchema?.sections.map((section: any, sIndex: number) => {
                  const isCollapsed = collapsedSections[section.id] !== false;
                  return (
                    <div key={section.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                      {/* Section Header */}
                      <div className="bg-slate-50 p-4 border-b border-gray-150 flex items-center justify-between gap-4 cursor-pointer" onClick={() => toggleSectionCollapse(section.id)}>
                        <div className="flex items-center gap-3 flex-1">
                          <span className="font-bold text-slate-800 text-md">
                            Section #{sIndex + 1}: {section.title || "Untitled Section"}
                          </span>
                          {!section.removable && (
                            <span className="px-2 py-0.5 text-[10px] font-bold text-slate-600 bg-slate-200 rounded uppercase">
                              Required / Core
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          {section.removable && (
                            <button
                              type="button"
                              onClick={() => deleteSection(section.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Section"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleSectionCollapse(section.id)}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            {isCollapsed ? <ChevronDown className="w-4.5 h-4.5" /> : <ChevronUp className="w-4.5 h-4.5" />}
                          </button>
                        </div>
                      </div>

                      {/* Section Body */}
                      <AnimatePresence initial={false}>
                        {!isCollapsed && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: "auto" }}
                            exit={{ height: 0 }}
                            className="p-6 border-t border-gray-100 space-y-6"
                          >
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500">Section Title</label>
                                <input
                                  type="text"
                                  value={section.title}
                                  onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                                  placeholder="e.g. Personal Details"
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-500">Description (Optional)</label>
                                <input
                                  type="text"
                                  value={section.description || ""}
                                  onChange={(e) => updateSectionDesc(section.id, e.target.value)}
                                  placeholder="Provide optional guidance text for user"
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </div>
                            </div>

                            {/* Section Fields list */}
                            <div className="space-y-4 pt-4 border-t border-gray-100">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-gray-700">Form Fields</h4>
                                <button
                                  type="button"
                                  onClick={() => addCustomField(section.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg border border-blue-100 text-xs font-semibold transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Custom Field
                                </button>
                              </div>

                              {section.fields.length === 0 ? (
                                <div
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(e, section.id, 0)}
                                  className="text-center py-6 border border-dashed border-gray-200 rounded-xl bg-gray-50 transition-colors hover:bg-blue-50/30 hover:border-blue-300"
                                >
                                  <span className="text-xs text-gray-400">No fields configured in this section. Drag a field here to add.</span>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {section.fields.map((field: any, fieldIndex: number) => {
                                    const isLocked = isCoreField(field.id);
                                    const isEditing = editingFieldId === `${section.id}.${field.id}`;
                                    return (
                                      <div
                                        key={field.id}
                                        draggable={!isEditing}
                                        onDragStart={(e) => handleDragStart(e, section.id, fieldIndex)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, section.id, fieldIndex)}
                                        onDragEnd={handleDragEnd}
                                        className={`p-4 border rounded-xl bg-slate-50 border-slate-200 space-y-3 transition-all ${
                                          draggedField && draggedField.sectionId === section.id && draggedField.index === fieldIndex
                                            ? "opacity-40 border-dashed border-blue-400 bg-blue-50/30 scale-[0.98] cursor-grabbing"
                                            : "hover:border-slate-350 hover:bg-slate-100/50 cursor-grab"
                                        }`}
                                      >
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                          <div className="flex items-center gap-2">
                                            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600 transition-colors mr-1" />
                                            <span className="font-bold text-sm text-gray-800">{field.label || "Unnamed Field"}</span>
                                            <span className="px-2 py-0.5 text-[9px] font-bold text-gray-500 bg-white border border-gray-200 rounded uppercase">
                                              {field.type}
                                            </span>
                                            {field.required && (
                                              <span className="px-1.5 py-0.5 text-[9px] font-bold text-red-600 bg-red-50 border border-red-100 rounded">
                                                Required
                                              </span>
                                            )}
                                            {isLocked && (
                                              <span className="px-2 py-0.5 text-[9px] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded">
                                                Locked Core
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-1.5">
                                            <button
                                              type="button"
                                              onClick={() => setEditingFieldId(isEditing ? null : `${section.id}.${field.id}`)}
                                              className="p-1 text-slate-600 hover:bg-slate-200 rounded-md transition-colors"
                                              title="Edit properties"
                                            >
                                              {isEditing ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                                            </button>
                                            {!isLocked && (
                                              <button
                                                type="button"
                                                onClick={() => deleteField(section.id, field.id)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                title="Delete field"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        {isEditing && (
                                          <div className="p-4 bg-white rounded-lg border border-gray-150 space-y-4">
                                            <div className="grid md:grid-cols-3 gap-4">
                                              <div className="space-y-1">
                                                <label className="block text-[11px] font-semibold text-gray-500">Field Label</label>
                                                <input
                                                  type="text"
                                                  value={field.label}
                                                  onChange={(e) => updateFieldProperty(section.id, field.id, "label", e.target.value)}
                                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                                                />
                                              </div>

                                              <div className="space-y-1">
                                                <label className="block text-[11px] font-semibold text-gray-500">Field Type</label>
                                                <select
                                                  value={field.type}
                                                  disabled={isLocked}
                                                  onChange={(e) => updateFieldProperty(section.id, field.id, "type", e.target.value)}
                                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs disabled:bg-gray-50"
                                                >
                                                  <option value="text">Single Line Text</option>
                                                  <option value="textarea">Multi-line Text (Textarea)</option>
                                                  <option value="number">Number</option>
                                                  <option value="date">Date</option>
                                                  <option value="email">Email Address</option>
                                                  <option value="phone">Mobile Phone</option>
                                                  <option value="select">Dropdown Select</option>
                                                  <option value="checkbox">Checkbox Toggle</option>
                                                  <option value="address">Sub-Address block</option>
                                                  <option value="file">PDF File Upload</option>
                                                  <option value="image">Image File Upload</option>
                                                  <option value="repeater">Repeater Rows list</option>
                                                </select>
                                              </div>

                                              <div className="space-y-4 pt-6">
                                                <label className="flex items-center gap-1.5 cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={!!field.required}
                                                    disabled={isLocked && field.required}
                                                    onChange={(e) => updateFieldProperty(section.id, field.id, "required", e.target.checked)}
                                                    className="w-3.5 h-3.5 text-blue-600 rounded"
                                                  />
                                                  <span className="text-[11px] font-semibold text-gray-600">Required Field</span>
                                                </label>
                                              </div>
                                            </div>

                                            {/* Dropdown Options List */}
                                            {field.type === "select" && (
                                              <div className="p-3 bg-slate-50 border rounded-lg space-y-2">
                                                <span className="block text-xs font-bold text-slate-700">Dropdown Options</span>
                                                <div className="flex gap-2">
                                                  <input
                                                    type="text"
                                                    id={`new-opt-${section.id}-${field.id}`}
                                                    placeholder="Add option text"
                                                    className="px-2 py-1 border rounded text-xs flex-1"
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Enter") {
                                                        const target = e.target as HTMLInputElement;
                                                        handleAddSelectOption(section.id, field.id, target.value);
                                                        target.value = "";
                                                      }
                                                    }}
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const el = document.getElementById(`new-opt-${section.id}-${field.id}`) as HTMLInputElement | null;
                                                      if (el) {
                                                        handleAddSelectOption(section.id, field.id, el.value);
                                                        el.value = "";
                                                      }
                                                    }}
                                                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-bold"
                                                  >
                                                    Add
                                                  </button>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 mt-2">
                                                  {(field.options || []).map((opt: string) => (
                                                    <span key={opt} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-white border text-xs text-gray-600 font-medium">
                                                      {opt}
                                                      <button
                                                        type="button"
                                                        onClick={() => handleRemoveSelectOption(section.id, field.id, opt)}
                                                        className="text-red-500 hover:text-red-700 font-bold ml-1 text-[10px]"
                                                      >
                                                        &times;
                                                      </button>
                                                    </span>
                                                  ))}
                                                  {(field.options || []).length === 0 && (
                                                    <span className="text-xs text-amber-600 font-medium">Add at least one selection option.</span>
                                                  )}
                                                </div>
                                              </div>
                                            )}

                                            {/* Numeric range validations */}
                                            {field.type === "number" && (
                                              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 border rounded-lg">
                                                <div className="space-y-1">
                                                  <label className="block text-[10px] font-bold text-slate-700">Minimum Value</label>
                                                  <input
                                                    type="number"
                                                    value={field.validations?.min !== undefined ? field.validations.min : ""}
                                                    onChange={(e) => updateFieldValidation(section.id, field.id, "min", e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-full px-2 py-1 border rounded text-xs"
                                                    placeholder="None"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="block text-[10px] font-bold text-slate-700">Maximum Value</label>
                                                  <input
                                                    type="number"
                                                    value={field.validations?.max !== undefined ? field.validations.max : ""}
                                                    onChange={(e) => updateFieldValidation(section.id, field.id, "max", e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-full px-2 py-1 border rounded text-xs"
                                                    placeholder="None"
                                                  />
                                                </div>
                                              </div>
                                            )}

                                            {/* Text constraints validations */}
                                            {(field.type === "text" || field.type === "textarea") && (
                                              <div className="grid grid-cols-3 gap-4 p-3 bg-slate-50 border rounded-lg">
                                                <div className="space-y-1">
                                                  <label className="block text-[10px] font-bold text-slate-700">Min. Length</label>
                                                  <input
                                                    type="number"
                                                    value={field.validations?.minLength !== undefined ? field.validations.minLength : ""}
                                                    onChange={(e) => updateFieldValidation(section.id, field.id, "minLength", e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-full px-2 py-1 border rounded text-xs"
                                                    placeholder="None"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="block text-[10px] font-bold text-slate-700">Max. Length</label>
                                                  <input
                                                    type="number"
                                                    value={field.validations?.maxLength !== undefined ? field.validations.maxLength : ""}
                                                    onChange={(e) => updateFieldValidation(section.id, field.id, "maxLength", e.target.value === "" ? "" : Number(e.target.value))}
                                                    className="w-full px-2 py-1 border rounded text-xs"
                                                    placeholder="None"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="block text-[10px] font-bold text-slate-700">Regex Pattern</label>
                                                  <input
                                                    type="text"
                                                    value={field.validations?.regex || ""}
                                                    onChange={(e) => updateFieldValidation(section.id, field.id, "regex", e.target.value)}
                                                    className="w-full px-2 py-1 border rounded text-xs font-mono"
                                                    placeholder="e.g. ^\d{10}$"
                                                  />
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer Actions / Errors */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 pt-6 gap-4 bg-white p-5 rounded-2xl border shadow-sm">
          <div>
            {error && <span className="text-sm font-semibold text-red-600 block">Error: {error}</span>}
            <span className="text-xs text-gray-500 block">
              Created recruitments are saved as Drafts until you choose to Publish them.
            </span>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex items-center gap-1.5 px-5 py-2.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-xl shadow-sm text-sm font-bold transition-all disabled:opacity-60"
            >
              <Save className="w-4.5 h-4.5" /> Save as Draft
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="flex items-center gap-1.5 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-sm text-sm font-bold transition-all disabled:opacity-60"
            >
              Save & Publish Recruitment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
